import { Buffer } from "buffer";
import jksreader from "jksreader";
import { Priv } from "jkurwa";

import {
  ERRORS,
  JKS_MAGIC,
  MEEDOC_HEADER_LENGTH,
  MEEDOC_HEADER_MARKER,
  UNPARSEABLE_KEY_STORE_PATTERN,
} from "@prostocheck/shared";

import { IDiagnoseParams } from "./key-box.models";

/** Some issuers hand out containers behind a short meedoc header. */
export const stripContainerHeader = (buffer: Buffer): Buffer =>
  buffer[0] === MEEDOC_HEADER_MARKER
    ? Buffer.from(buffer.subarray(MEEDOC_HEADER_LENGTH))
    : buffer;

/**
 * jkurwa answers every failure with the same "Cant load key from store", which
 * leaves the user unable to tell a wrong password from a wrong file — the two
 * mistakes people actually make. Reproduce the parse to find out which it was,
 * and return the translation key that says so.
 */
export const diagnoseKeyError = ({
  content,
  password,
  algo,
}: IDiagnoseParams): string => {
  const jks = jksreader.parse(content);

  if (jks) {
    const [material] = jks.material ?? [];

    if (material && !jksreader.decode(material.key, password)) {
      return ERRORS.key.wrongPassword;
    }

    return ERRORS.common.unknown;
  }

  // A .jks written by a modern keytool is really PKCS#12 behind a JKS magic.
  if (Buffer.from(content.subarray(0, 4)).toString("hex") === JKS_MAGIC) {
    return ERRORS.key.unsupportedContainer;
  }

  try {
    Priv.from_protected(content, password, algo);
  } catch (parseError) {
    const message = parseError instanceof Error ? parseError.message : "";

    // Failing at the container level means the format is foreign; any other
    // ASN.1 error means it was recognised and the decrypted bytes are garbage.
    if (UNPARSEABLE_KEY_STORE_PATTERN.test(message)) {
      return ERRORS.key.unsupportedContainer;
    }

    return ERRORS.key.wrongPassword;
  }

  return ERRORS.common.unknown;
};
